import { type FC, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';
import { AnimatePresence, motion } from 'framer-motion';

import {
  H2,
  StringMain,
  StringXSmall,
} from '@/components/Typography/Typography';

import css from './styles.module.scss';

interface BingoInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  endAtLabel?: string;
}

interface InfoSection {
  icon: string;
  title: string;
  body: ReactNode;
}

const BingoInfoModal: FC<BingoInfoModalProps> = ({
  isOpen,
  onClose,
  endAtLabel,
}) => {
  const { t } = useTranslation();

  const sections: InfoSection[] = [
    {
      icon: '🎯',
      title: t('How Bingo Works'),
      body: t(
        'The event runs every Saturday and Sunday. Throw balls, reveal random tiles on the board, and collect rewards instantly.',
      ),
    },
    {
      icon: '🔁',
      title: t('Line Rewards'),
      body: t(
        'Complete rows, columns, and diagonals to earn rewards. Each new card resets the lines, so you can earn rewards again.',
      ),
    },
    {
      icon: '🏆',
      title: t('Main Goal'),
      body: t(
        'Fill the entire card (all 25 tiles) to unlock the chest. Choose one of three rewards and claim your prize.',
      ),
    },
    {
      icon: '🔴',
      title: t('Where to Get Balls'),
      body: (
        <>
          <StringXSmall className={css.itemDesc}>
            {/* The native app sells balls via the App Store only */}
            {Capacitor.isNativePlatform()
              ? t(
                  'Complete contracts to earn balls: up to 100 balls per day 5 balls per contract. You can also buy balls in the shop.',
                )
              : t(
                  'Complete contracts to earn balls: up to 100 balls per day 5 balls per contract. You can also buy balls in the shop using Stars or TON.',
                )}
          </StringXSmall>
        </>
      ),
    },
    {
      icon: '⏳',
      title: t('After the Event Ends'),
      body: (
        <>
          <StringXSmall className={css.itemDesc}>
            {t(
              'You can complete an unlimited number of cards during the event. All unused balls are converted into dice (10:1).',
            )}
          </StringXSmall>
          {endAtLabel ? (
            <StringXSmall className={css.itemAccent}>
              {t('Time left')}: {endAtLabel}
            </StringXSmall>
          ) : null}
        </>
      ),
    },
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className={css.backdrop}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
          />

          <motion.div
            className={css.sheet}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ duration: 0.32, ease: [0.32, 0.72, 0, 1] }}
          >
            <div className={css.handle} />

            <div className={css.header}>
              <div className={css.headerIcon}>
                <img
                  src="/img/bingo/bingo-icon.png"
                  alt=""
                  className={css.headerIconImg}
                />
              </div>
              <div className={css.headerText}>
                <H2 weight="bold" className={css.headerTitle}>
                  {t('About Bingo event')}
                </H2>
                <StringXSmall className={css.headerSub}>
                  {t('Rules, rewards and timing')}
                </StringXSmall>
              </div>
            </div>

            <div className={css.list}>
              {sections.map((section, idx) => (
                <motion.div
                  key={section.title}
                  className={css.item}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.06 + idx * 0.04, duration: 0.25 }}
                >
                  <span className={css.itemIcon}>{section.icon}</span>
                  <div className={css.itemText}>
                    <StringMain weight="bold" className={css.itemTitle}>
                      {section.title}
                    </StringMain>
                    {typeof section.body === 'string' ? (
                      <StringXSmall className={css.itemDesc}>
                        {section.body}
                      </StringXSmall>
                    ) : (
                      section.body
                    )}
                  </div>
                </motion.div>
              ))}
            </div>

            <button className={css.cancel} onClick={onClose}>
              <StringMain className={css.cancelText}>{t('Got it')}</StringMain>
            </button>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export { BingoInfoModal };
